import React, { Fragment, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/solid";
import { useTranslation } from "react-i18next";
import { getPdfSimpliBaseURL } from "../../services/api-constants";

const HelpGuideModal = ({ toggleHelpGuide, isHelpGuideVisible }) => {
  const { t } = useTranslation();
  const { locale } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const containerRef = useRef(null);

  const htmlUrl = locale
    ? `https://static-bw.worksimpli.io/welcome-guide/welcomeguide_${locale}.htm`
    : "https://static-bw.worksimpli.io/welcome-guide/welcomeguide.htm";

  useEffect(() => {
    if (!isHelpGuideVisible) return;

    let cancelled = false;

    async function loadHtmlIntoShadow() {
      setIsLoading(true);
      try {
        const res = await fetch(htmlUrl, { credentials: "omit" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const htmlText = await res.text();

        const host = containerRef.current;
        if (!host) return;
        // Create shadow root once
        const shadow = host.shadowRoot || host.attachShadow({ mode: "open" });

        // Build a <base> so relative URLs (css/img/links) resolve correctly
        const baseHref = htmlUrl.replace(/[^/]*$/, ""); // directory of the file
        const withBase = `<base href="${baseHref}">` + htmlText;

        // Set content (styles are isolated inside the shadow root)
        shadow.innerHTML = withBase;

        // Re-execute any <script> tags (innerHTML won't run them)
        const scripts = shadow.querySelectorAll("script");
        scripts.forEach((oldScript) => {
          const newScript = document.createElement("script");
          // Copy attributes (type, src, etc.)
          for (const { name, value } of Array.from(oldScript.attributes)) {
            newScript.setAttribute(name, value);
          }
          // Inline script content
          newScript.textContent = oldScript.textContent;
          oldScript.replaceWith(newScript);
        });

        if (!cancelled) setIsLoading(false);
      } catch (err) {
        console.error("Failed to load help guide:", err);
        if (!cancelled) setIsLoading(false);
      }
    }

    loadHtmlIntoShadow();
    return () => {
      cancelled = true;
    };
  }, [htmlUrl, isHelpGuideVisible]);

  return (
    <Transition.Root show={isHelpGuideVisible} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={toggleHelpGuide}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-0 md:p-4 text-center sm:items-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 w-4/5 sm:max-w-2xl sm:p-6">
                <div className="absolute top-0 right-0 pt-5 pr-4 sm:block rtl:left-0 rtl:pl-2">
                  <button
                    type="button"
                    className="rounded-md bg-white text-gray-400 hover:text-gray-500"
                    onClick={() => toggleHelpGuide(false)}
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>

                <div className="text-gray-500">
                  <div className="pb-4">
                    <Dialog.Title className="flex text-2xl leading-6 text-gray-600 pb-2 border-b-2 border-gray-100 !font-bold">
                      {t("generic_text.help_guide")}
                    </Dialog.Title>
                  </div>
                </div>

                {isLoading && (
                  <div className="flex justify-center items-center h-96">
                    <div className="loader ease-linear rounded-full border-4 border-t-4 border-gray-200 h-12 w-12 mb-4 animate-spin border-t-blue-500" />
                  </div>
                )}

                {/* Replaces the iframe */}
                <div className={`${isLoading ? "hidden" : "block"}`}>
                  {/* This host will contain the Shadow DOM with the fetched HTML */}
                  <div
                    ref={containerRef}
                    // control the viewport of the embedded content
                    style={{ display: "block", width: "100%", height: 400, overflow: "auto" }}
                  />
                </div>

                <div className="mt-5 sm:mt-4 grid-cols-2 justify-between sm:flex sm:flex-row-reverse border-t-2">
                  <button
                    type="button"
                    className="inline-flex w-full justify-center rounded-md bg-gray-500 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm mt-2"
                    onClick={() => toggleHelpGuide(false)}
                  >
                    {t("payment_error.close")}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default HelpGuideModal;
